import {
  CircularProgress,
  Menu,
  MenuItem,
  SvgIcon,
  Tooltip,
  useTheme,
} from "@mui/material";
import { t } from "i18next";
import Q3DIconButton from "../../Parts/Q3DIconButton";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  toolBoxIcon_1,
  BLACK,
  toolBoxIcon_2,
  WHITE,
  toolBoxIcon_3,
} from "../../config/componentsConfig";
import {
  DETAIL_TAB,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RouteIcon from "@mui/icons-material/Route";
import ToolIcon from "../../../components/Icon";
import CoordinateIcon from "../../../../images/icons/neutral_edit/Coordinate.svg";
import ImageViewerDialog from "../../PhotoshotDialog";
import { config } from "../../../config/config";
import { Common } from "../../../../common/src";
import DataDesign from "../../../../images/icons/cubes.svg";
import DataDesignWhite from "../../../../images/icons/white_edit/cubes_white.svg";
import { view } from "paper/dist/paper-core";
import { projectARFileApis } from "../../../api/project";
import { observe } from "../../../utils/observe";

type Props = {
  projectDetail: Common.ProjectDetail;
  onChangeIfcState?: (isCentered: boolean) => void;
};

export const ShowToolMenu = (props: Props) => {
  const theme = useTheme();

  const [visibilityAnchorEl, setVisibilityAnchorEl] = useState<Element>(null); // menu anchor
  const [showAxis, setShowAxis] = useState<boolean>(true); // axis
  const [showPointCloud, setShowPointCloud] = useState<boolean>(true); // pointCloud
  const [showPhotoshot, setShowPhotoshot] = useState<boolean>(true); // photoshot
  const [showIFC, setShowIFC] = useState<boolean>(false); // IFC
  const [ifcLoading, setIfcLoading] = useState<boolean>(false); // IFC loading state
  const ifcFileUrlRef = useRef<string | null>(null);
  const openVisibilityMenu = (event: any) => {
    setVisibilityAnchorEl(event.currentTarget);
  };
  const {
    viewer,
    openProjectState,
    setOpenProjectState,
    detailTab,
    projectDetail,
    isArView,
    isIosView,
  } = useContext(ProjectDetailContext);
  const btnSize = "36px";
  const DARK = "dark";

  const closeVisibilityMenu = () => {
    setVisibilityAnchorEl(null);
  };

  const toggleAxisVisiblity = () => {
    setShowAxis(!showAxis);
    viewer.setAxisVisiblity(!showAxis);
  };

  const toggleIFC = () => {
    setShowIFC(!showIFC);
  };

  useEffect(() => {
    if (ifcFileUrlRef.current) {
      viewer.designURL = null;
      URL.revokeObjectURL(ifcFileUrlRef.current);
      ifcFileUrlRef.current = null;
    }

    if (
      !!props.projectDetail.ifc_file &&
      props.projectDetail.useViDoc !== false
    ) {
      setShowIFC(true);
    }
  }, []);

  //Hide file and revoke URL when ifc_file is null after delete
  useEffect(() => {
    if (ifcFileUrlRef.current && !props.projectDetail.ifc_file) {
      URL.revokeObjectURL(ifcFileUrlRef.current);
      ifcFileUrlRef.current = null;
      setShowIFC(false);
      viewer.designURL = null;
    }

    if (isArView && !props.projectDetail.las_file) {
      setShowPointCloud(false);
      viewer.setPointCloudVisibility(false);
    }

    if (
      isArView &&
      !props.projectDetail.las_file &&
      !!props.projectDetail.ifc_file
    ) {
      setShowIFC(true);
    }
  }, [props.projectDetail]);

  useEffect(() => {
    const fetchIFC = async () => {
      if (
        !showIFC ||
        ifcFileUrlRef.current ||
        (!isArView && detailTab !== DETAIL_TAB.SFM) ||
        !projectDetail ||
        !projectDetail.ifc_file ||
        !projectDetail.useViDoc
      )
        return;

      try {
        const response = await projectARFileApis.getFileUrl.get(
          projectDetail.id,
          Common.AR_FILE_ACCEPTED_TYPE.IFC,
        );
        const presignedUrl = response.data;
        ifcFileUrlRef.current = presignedUrl;
        viewer.designURL = presignedUrl;
      } catch (error) {
        console.error("Failed to load IFC file:", error);
      }
    };

    fetchIFC();
  }, [showIFC, projectDetail.id]);

  useEffect(() => {
    //hide IFC when not in SFM nor AR view
    if (!isArView && detailTab !== DETAIL_TAB.SFM) viewer.designURL = null;

    if (showIFC) {
      //skip setUrl when ifcFileUrl is not ready
      if (!!ifcFileUrlRef.current) viewer.designURL = ifcFileUrlRef.current;
    } else {
      viewer.designURL = null;
    }
    props.onChangeIfcState && props.onChangeIfcState(showIFC);
  }, [showIFC, detailTab]);

  useEffect(() => {
    if (ifcFileUrlRef.current && !props.projectDetail.ifc_file) {
      URL.revokeObjectURL(ifcFileUrlRef.current);
      ifcFileUrlRef.current = null;
      setShowIFC(false);
      viewer.designURL = null;
    }
  }, [props.projectDetail]);

  const togglePhotoshotVisiblity = () => {
    setShowPhotoshot(!showPhotoshot);
    viewer.setPhotoshotVisible(!showPhotoshot);
  };

  // const disabledToggleAxis = () => {
  //   return whilePointCloudLoading;
  // }

  const toggleShowPointcloud = () => {
    setShowPointCloud(!showPointCloud);
    viewer.setPointCloudVisibility(!showPointCloud);
  };

  const toggleProjectState = () => {
    // hideAllMenus();
    setOpenProjectState(!openProjectState);
  };

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [imageInfo, setImageInfo] = useState<{ name: string; src: string }>({
    name: "",
    src: "",
  });

  useEffect(() => {
    const handlePhotoshotClick = (e: any) => {
      const { photoName } = e.detail;
      let hostName = config.serverHost.includes("localhost")
        ? "http://localhost:9090/quick3d"
        : config.serverHost;
      const imageUrl = `${hostName}/project_${props.projectDetail.id}/${photoName}`;
      setImageInfo({ name: photoName, src: imageUrl });
      setOpenImageDialog(true);
    };

    window.addEventListener("photoshot-clicked", handlePhotoshotClick);
    return () => {
      window.removeEventListener("photoshot-clicked", handlePhotoshotClick);
    };
  }, []);

  observe(viewer, ["designStatus"], () => {
    console.log("load ifc state", viewer.designStatus);

    setIfcLoading(viewer.designStatus === "loading");
  });

  return (
    <>
      <Q3DIconButton
        title={t("SHOW_HIDE")}
        titleType="tooltip"
        onClick={(e) => {
          openVisibilityMenu(e);
        }}
        icon={
          <SvgIcon>
            <path d={toolBoxIcon_1} fill={BLACK} />
            <path d={toolBoxIcon_2} fill={WHITE} />
            <path d={toolBoxIcon_3} fill={WHITE} />
          </SvgIcon>
        }
        height={btnSize}
        width={btnSize}
        disabled={detailTab === DETAIL_TAB.PHOTO}
      />
      <Menu
        id="simple-menu"
        keepMounted
        anchorEl={visibilityAnchorEl}
        open={Boolean(visibilityAnchorEl)}
        onClose={closeVisibilityMenu}
        style={{ borderRadius: "0px" }}
        // PaperProps={{ style: { ...customBackground, ...customColor } }}
      >
        {((!isArView && detailTab !== DETAIL_TAB.PHOTO) ||
          (isArView && projectDetail.las_file !== null)) && (
          <MenuItem
            value="focusBelow"
            className="point-view4"
            // disabled={disabledToggleAxis()}
            onClick={toggleShowPointcloud}
          >
            <Tooltip
              title={
                <span className="point-view4-tool-tip">
                  {t("DISPLAY_POINT_GROUP")}
                </span>
              }
              arrow
              placement="left"
              disableInteractive
            >
              <span>
                <SvgIcon>
                  <path d={toolBoxIcon_1} fill={BLACK} />
                  <path d={toolBoxIcon_2} fill={WHITE} />
                  <path d={toolBoxIcon_3} fill={WHITE} />
                </SvgIcon>
              </span>
            </Tooltip>
          </MenuItem>
        )}
        <MenuItem
          value="focusBelow"
          className="point-view4"
          // disabled={disabledToggleAxis()}
          onClick={toggleAxisVisiblity}
        >
          <Tooltip
            title={
              <span className="point-view4-tool-tip">{t("DISPLAY_AXIS")}</span>
            }
            arrow
            placement="left"
            disableInteractive
          >
            <span>
              <ToolIcon
                imagePath={CoordinateIcon}
                color={
                  // disabledToggleAxis() || !showAxis ? "gray" : customColor.color
                  customColor.color
                }
              />
            </span>
          </Tooltip>
        </MenuItem>
        {!isArView && (
          <MenuItem onClick={toggleProjectState}>
            <Tooltip
              title={
                <span className="point-view4-tool-tip">
                  {t("DISPLAY_STATUS")}
                </span>
              }
              arrow
              placement="left"
              disableInteractive
            >
              <FactCheckIcon
                sx={{
                  color: customColor.color,
                }}
              />
            </Tooltip>
          </MenuItem>
        )}
        {!isArView && (
          <MenuItem onClick={togglePhotoshotVisiblity}>
            <Tooltip
              title={
                <span className="point-view4-tool-tip">
                  {t("DISPLAY_PHOTOSHOT")}
                </span>
              }
              arrow
              placement="left"
              disableInteractive
            >
              <span>
                <RouteIcon
                  style={{
                    transform: "rotate(45deg)",
                  }}
                />
              </span>
            </Tooltip>
          </MenuItem>
        )}

        <MenuItem
          onClick={toggleIFC}
          disabled={
            ifcLoading ||
            !projectDetail.ifc_file ||
            projectDetail.useViDoc === false
          }
        >
          <Tooltip
            title={
              <span className="point-view4-tool-tip">
                {t("DISPLAY_DESIGN_DATA")}
              </span>
            }
            arrow
            placement="left"
            disableInteractive
          >
            <span style={{ marginLeft: "3px" }}>
              <ToolIcon
                imagePath={DataDesign}
                color={
                  projectDetail.ifc_file === null ||
                  projectDetail.useViDoc === false
                    ? "gray"
                    : customColor.color
                }
                width={22}
                height={22}
              />
              {ifcLoading && (
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    top: "0",
                    left: "0",
                    backgroundColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  <CircularProgress size={30} />
                </div>
              )}
            </span>
          </Tooltip>
        </MenuItem>
      </Menu>
      {showPhotoshot && (
        <ImageViewerDialog
          open={openImageDialog}
          setOpen={setOpenImageDialog}
          imageSrc={imageInfo.src}
          imageName={imageInfo.name}
        />
      )}
    </>
  );
};
